// ── Style tokens ────────────────────────────────────────────────────────────
const S = {
  wrapper: {
    marginTop: '10px', padding: '40px', border: '3px solid #1a5c2a',
    fontFamily: "'Georgia', serif", color: '#0a1f0e', lineHeight: '1.7',
    background: '#f2faf4',
  },
  h1: {
    fontSize: '2rem', fontWeight: '700', borderBottom: '3px solid #2e8b47',
    paddingBottom: '8px', marginBottom: '20px', marginTop: '32px', color: '#145a24',
  },
  subtitle: {
    fontSize: '0.95rem', color: '#3a7a4a', marginTop: '16px', marginBottom: '28px', fontStyle: 'italic',
  },
  h2: {
    fontSize: '1.3rem', fontWeight: '700', color: '#1e7a36', marginTop: '36px',
    marginBottom: '12px', borderLeft: '4px solid #2e8b47', paddingLeft: '10px',
  },
  h3: {
    fontSize: '1.05rem', fontWeight: '700', color: '#2a6a3a', marginTop: '20px',
    marginBottom: '8px',
  },
  p: { marginBottom: '12px', fontSize: '0.97rem' },
  infoBox: {
    background: '#e8f5ec', border: '2px solid #2e8b47', borderRadius: '6px',
    padding: '16px 20px', margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700', fontFamily: "'Courier New', monospace", fontSize: '0.88rem',
    color: '#145a24', marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem', margin: '4px 0', paddingLeft: '12px',
    borderLeft: '2px solid #2e8b47',
  },
  legendBox: {
    display: 'flex', flexWrap: 'wrap', gap: '12px', margin: '16px 0 20px 0',
    background: '#dff0e4', border: '1px solid #7abf8a', borderRadius: '6px',
    padding: '12px 16px',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' },
  legendDot: { width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0 },
  noteBox: {
    background: '#fff9e6', border: '1px solid #c8a800', borderRadius: '5px',
    padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem', color: '#5a4500',
  },
  dangerBox: {
    background: '#fde8e8', border: '1px solid #c83030', borderRadius: '5px',
    padding: '10px 14px', margin: '12px 0', fontSize: '0.88rem', color: '#5a0000',
  },
};

// ── Rating color coding ──────────────────────────────────────────────────────
const RATING_COLORS = {
  'Required':    { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Recommended': { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Situational': { bg: '#d4edda', text: '#1c5a2a', border: '#6abf7a' },
  'Optional':    { bg: '#e9ecef', text: '#555', border: '#aaa' },
  'Excellent':   { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'Good':        { bg: '#d4edda', text: '#1c5a2a', border: '#6abf7a' },
  'Moderate':    { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Limited':     { bg: '#fde2b8', text: '#7a3500', border: '#ff9800' },
  'Poor':        { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Yes':         { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
  'No':          { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Partial':     { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'High':        { bg: '#f8d7da', text: '#7a0010', border: '#f44336' },
  'Medium':      { bg: '#fff3cd', text: '#6a4a00', border: '#ffc107' },
  'Low':         { bg: '#c6efce', text: '#1a5c1a', border: '#4caf50' },
};

function RatingBadge({ value }) {
  const key = Object.keys(RATING_COLORS).find(k => value && value.startsWith(k));
  const style = key ? RATING_COLORS[key] : { bg: '#e9ecef', text: '#444', border: '#bbb' };
  return (
    <span style={{
      background: style.bg, color: style.text, border: `1px solid ${style.border}`,
      borderRadius: '4px', padding: '2px 7px', fontSize: '0.82rem', fontWeight: '600',
      whiteSpace: 'nowrap', display: 'inline-block',
    }}>{value}</span>
  );
}

// ── Reusable two-column property table ──────────────────────────────────────
function Table2({ rows }) {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '10px 14px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.9rem',
  };
  const tdBase = {
    padding: '10px 14px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.9rem', fontFamily: "'Courier New', monospace", whiteSpace: 'pre-wrap',
  };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
      <thead><tr>
        <th style={{ ...thStyle, width: '240px' }}>Property</th>
        <th style={thStyle}>Detail</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#145a24', fontFamily: "'Georgia', serif" }}>{label}</td>
            <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

// ── PPE item data ────────────────────────────────────────────────────────────
const PPE_ITEMS = [
  {
    name: 'Safety Glasses',
    category: 'Eye & Face Protection',
    standard: 'ANSI Z87.1 / EN ISO 16321',
    hazard: 'Flying debris, chips, dust, splash',
    protection: 'Impact, splash',
    heatResist: 'Limited',
    chemResist: 'Moderate',
    requiredFor: 'All machining, grinding, drilling, cutting, sanding',
    notFor: 'Molten metal, heavy grinding without side shields',
    fitNotes: 'Must have side shields; ensure wraparound fit; prescription versions available',
    comfortRating: 'Excellent',
    durationLimit: 'All-day wear acceptable',
  },
  {
    name: 'Safety Goggles',
    category: 'Eye & Face Protection',
    standard: 'ANSI Z87.1 / EN 166',
    hazard: 'Chemical splash, fine dust, mist, molten fragments',
    protection: 'Full seal around eyes; splash + impact',
    heatResist: 'Moderate',
    chemResist: 'Good',
    requiredFor: 'Welding prep, chemical use, turning/milling with coolant, concrete work',
    notFor: 'Molten metal pouring (use full face shield)',
    fitNotes: 'Indirect-vent for splash; direct-vent for dust only; fog-resistant coating recommended',
    comfortRating: 'Moderate',
    durationLimit: 'Fogging limits continuous wear; take breaks',
  },
  {
    name: 'Face Shield',
    category: 'Eye & Face Protection',
    standard: 'ANSI Z87.1 / EN 166 + EN 170',
    hazard: 'Heavy impact, molten splash, grinding sparks',
    protection: 'Full face: forehead to chin',
    heatResist: 'Good',
    chemResist: 'Good',
    requiredFor: 'Angle grinding, turning, lathe work, chemical decanting, plasma cutting',
    notFor: 'Primary eye protection on its own — must be worn over safety glasses',
    fitNotes: 'Adjust headband so shield sits 1" from face; flip-up models convenient for intermittent use',
    comfortRating: 'Moderate',
    durationLimit: 'Heavy grinding: replace shield when visibly scratched (reduces vision clarity)',
  },
  {
    name: 'Welding Helmet',
    category: 'Eye & Face Protection',
    standard: 'ANSI Z49.1 / EN 175 / DIN shade scale',
    hazard: 'UV/IR radiation, molten spatter, arc flash',
    protection: 'UV + IR + spatter + heat',
    heatResist: 'Excellent',
    chemResist: 'Good',
    requiredFor: 'MIG, TIG, Stick, plasma welding/cutting',
    notFor: 'General grinding without welding',
    fitNotes: 'Auto-darkening: shade 9–13 depending on process; fixed shade must be selected per amperage table',
    comfortRating: 'Good',
    durationLimit: 'Check battery and lens integrity every session',
  },
  {
    name: 'Earplugs (Foam / Pre-moulded)',
    category: 'Hearing Protection',
    standard: 'ANSI S3.19 / EN 352-2; NRR or SNR rating',
    hazard: 'Continuous noise >85 dB TWA',
    protection: 'NRR 22–33 dB (foam); 20–26 dB (pre-moulded)',
    heatResist: 'N/A',
    chemResist: 'N/A',
    requiredFor: 'Angle grinders, hammering, routers, impact tools, sheet metal work',
    notFor: 'Situations requiring voice communication without PA system',
    fitNotes: 'Foam: roll, insert, hold 20–30 s for full expansion; pre-moulded: size selection critical',
    comfortRating: 'Good',
    durationLimit: 'Foam: single-use; pre-moulded: clean daily, replace if cracked',
  },
  {
    name: 'Earmuffs',
    category: 'Hearing Protection',
    standard: 'ANSI S3.19 / EN 352-1; NRR rating',
    hazard: 'Intermittent high-impulse noise; >85 dB environments',
    protection: 'NRR 22–31 dB; easy on/off for intermittent tasks',
    heatResist: 'N/A',
    chemResist: 'N/A',
    requiredFor: 'Power hammers, forging, intermittent grinder use, woodworking shop',
    notFor: 'Worn over safety glasses (seal compromised); hot environments',
    fitNotes: 'Headband must seal cups fully against skull; spectacle arms break the seal — use low-profile glasses',
    comfortRating: 'Moderate',
    durationLimit: 'Cushions harden after 6–12 months; inspect seal integrity quarterly',
  },
  {
    name: 'Dust Mask (N95 / FFP2)',
    category: 'Respiratory Protection',
    standard: 'NIOSH 42 CFR 84 / EN 149:2001',
    hazard: 'Nuisance dust, non-toxic particles, wood dust, metal fines',
    protection: '≥95% (N95) or ≥94% (FFP2) filtration of 0.3 µm particles',
    heatResist: 'Limited',
    chemResist: 'Poor — no vapour protection',
    requiredFor: 'Sanding, grinding metal/wood/concrete, sweeping, fibreglass cutting',
    notFor: 'Chemical vapours, paint fumes, welding fumes (use P100/OV respirator)',
    fitNotes: 'Perform user seal check each time; facial hair prevents proper seal; OSHA medical eval required for continued use',
    comfortRating: 'Good',
    durationLimit: 'Replace when damaged, wet, dirty, or breathing resistance increases',
  },
  {
    name: 'Half-Face Respirator (OV/P100)',
    category: 'Respiratory Protection',
    standard: 'NIOSH / EN 140; cartridge-specific standards',
    hazard: 'Chemical vapours, paint fumes, welding smoke, isocyanates',
    protection: 'Organic vapour + P100 particulate combination cartridges',
    heatResist: 'Moderate',
    chemResist: 'Excellent (cartridge-dependent)',
    requiredFor: 'Spray painting, epoxy/resin work, solvent degreasing, welding (Mn-containing),  chemical stripping',
    notFor: 'Oxygen-deficient environments; IDLH atmospheres (use SCBA)',
    fitNotes: 'Annual quantitative fit test required; change cartridges per OSHA change-out schedule or end-of-service-life indicator',
    comfortRating: 'Moderate',
    durationLimit: 'OV cartridges: replace per schedule; P100 replace when breathing resistance increases',
  },
  {
    name: 'Powered Air-Purifying Respirator (PAPR)',
    category: 'Respiratory Protection',
    standard: 'NIOSH 42 CFR 84 / EN 12941',
    hazard: 'High-concentration particulates, heavy welding fume, beryllium, lead dust',
    protection: 'APF 25 (half-mask) to 1000 (full hood); powered airflow eliminates fit concerns',
    heatResist: 'Moderate',
    chemResist: 'Excellent (filter dependent)',
    requiredFor: 'Lead/beryllium grinding, thermal spray, shipyard welding, extended welding shifts',
    notFor: 'Confined spaces without O₂ monitoring; IDLH; extreme temperatures affecting blower',
    fitNotes: 'No fit test required for hood/helmet style; battery charge state critical — inspect before each use',
    comfortRating: 'Excellent',
    durationLimit: 'Filter change per manufacturer schedule; blower battery ~4–8 hours per charge',
  },
  {
    name: 'Leather Work Gloves',
    category: 'Hand Protection',
    standard: 'EN 388 / ANSI/ISEA 105',
    hazard: 'Abrasion, puncture, heat, rough material handling',
    protection: 'Abrasion: EN 388 Level 4; puncture: Level 3; heat: limited',
    heatResist: 'Good',
    chemResist: 'Poor',
    requiredFor: 'Sheet metal handling, structural steel, casting, rough lumber, general material handling',
    notFor: 'Rotating machinery (entanglement risk); chemical handling; electrical work',
    fitNotes: 'Snug fit without excess material; gauntlet cuff for forearm protection near sparks',
    comfortRating: 'Good',
    durationLimit: 'Replace when worn thin on palm or finger seams fail',
  },
  {
    name: 'Cut-Resistant Gloves (ANSI A4–A9)',
    category: 'Hand Protection',
    standard: 'ANSI/ISEA 105 / EN 388 (ISO 13997)',
    hazard: 'Sharp edges, blades, sheet metal, glass, metal burrs',
    protection: 'Cut resistance A4 = 500–999 g; A9 = ≥6000 g on ANSI TDM-100',
    heatResist: 'Limited (HPPE/steel fibre blend)',
    chemResist: 'Moderate',
    requiredFor: 'Sheet metal fabrication, stamping press work, glass handling, blade changing',
    notFor: 'Rotating machinery; coated versions only for wet chemical handling',
    fitNotes: 'Higher ANSI cut level reduces dexterity; select minimum adequate level; thin liner under heavy gloves improves fit',
    comfortRating: 'Moderate',
    durationLimit: 'Inspect for cut-through in palm area; HPPE fibres can degrade with abrasion',
  },
  {
    name: 'Heat-Resistant Gloves (Welding / Foundry)',
    category: 'Hand Protection',
    standard: 'EN 407 / EN 12477 (welding)',
    hazard: 'Radiant heat, molten spatter, hot materials up to 250–1000 °C',
    protection: 'Contact heat: EN 407 Levels 1–4; welding spatter resistance',
    heatResist: 'Excellent',
    chemResist: 'Poor',
    requiredFor: 'Welding, plasma cutting, forging, furnace loading, hot work',
    notFor: 'Precision hand work; chemical environments; rotating machinery',
    fitNotes: 'Leather welding gloves: gauntlet length; aluminised: use for radiant heat >500 °C; kevlar for sustained contact',
    comfortRating: 'Moderate',
    durationLimit: 'Inspect for charring, holes, or stiffening after each use; replace when contact heat protection degrades',
  },
  {
    name: 'Chemical-Resistant Gloves (Nitrile / Neoprene / Butyl)',
    category: 'Hand Protection',
    standard: 'EN 374 / ASTM D6978',
    hazard: 'Solvents, acids, bases, cutting fluids, oils',
    protection: 'Material-specific: nitrile for oils/fuels; neoprene for acids/bases; butyl for ketones/esters',
    heatResist: 'Limited',
    chemResist: 'Excellent (material-matched)',
    requiredFor: 'Solvent degreasing, acid etching, coolant handling, epoxy mixing, paint stripping',
    notFor: 'Rotating machinery; mechanical cut hazards without additional liner',
    fitNotes: 'Always consult glove permeation data for specific chemical — breakthrough time varies widely; double-glove for IDLH chemicals',
    comfortRating: 'Good',
    durationLimit: 'Nitrile: inspect for micro-tears; neoprene: check for stiffness; butyl: inspect for tackiness',
  },
  {
    name: 'Safety Footwear (Steel / Composite Toe)',
    category: 'Foot Protection',
    standard: 'ASTM F2413 / EN ISO 20345 (S1–S5)',
    hazard: 'Falling objects, compression, puncture, slip, heat',
    protection: 'Toe: 75 ft-lb impact / 2500 lb compression (ASTM); sole puncture ≥1100 N',
    heatResist: 'Moderate (oil-resistant sole)',
    chemResist: 'Limited',
    requiredFor: 'All workshop environments: machining, welding, fabrication, assembly, material handling',
    notFor: 'Electrical work (ensure EH-rated); static-sensitive environments (use ESD-rated)',
    fitNotes: 'Steel vs composite toe: composite is lighter and non-conductive; metatarsal guard optional for dropped material risk; EH rating critical near live circuits',
    comfortRating: 'Good',
    durationLimit: 'Replace sole when tread depth <1.6 mm or when toe cap is deformed',
  },
  {
    name: 'Metatarsal Guards',
    category: 'Foot Protection',
    standard: 'ASTM F2413 (Mt75) / EN ISO 20345 (M class)',
    hazard: 'Heavy objects falling on instep (top of foot)',
    protection: 'External or internal guard: 75 ft-lb impact on metatarsal',
    heatResist: 'Moderate',
    chemResist: 'Limited',
    requiredFor: 'Heavy forging, foundry, large castings, logging equipment areas, heavy press work',
    notFor: 'Light assembly; environments with trip hazard from external guard strap',
    fitNotes: 'External strap-on: fits over any boot; internal: integrated into boot design preferred for comfort',
    comfortRating: 'Moderate',
    durationLimit: 'Inspect for cracks in guard after impact events',
  },
  {
    name: 'Hard Hat / Safety Helmet',
    category: 'Head Protection',
    standard: 'ANSI Z89.1 / EN 397',
    hazard: 'Falling objects, overhead impact, bump hazards',
    protection: 'Class E: 20 kV electrical; Class G: 2.2 kV; Class C: non-electrical; Type II: lateral impact',
    heatResist: 'Moderate',
    chemResist: 'Moderate',
    requiredFor: 'Overhead crane areas, construction bays, structural assembly, hot work with overhead hazard',
    notFor: 'Rotating machinery (chin strap can entangle); areas with severe chemical exposure (check rating)',
    fitNotes: 'Replace after any impact even if undamaged visually; replace every 5 years regardless; UV degrades HDPE shell',
    comfortRating: 'Moderate',
    durationLimit: '5-year replacement for shell; suspension system: 1-year replacement',
  },
  {
    name: 'Bump Cap',
    category: 'Head Protection',
    standard: 'EN 812',
    hazard: 'Low-clearance bumps, minor lacerations on stationary objects',
    protection: 'Bump protection only — NOT for falling objects',
    heatResist: 'Limited',
    chemResist: 'Limited',
    requiredFor: 'Under-vehicle maintenance, confined equipment spaces, low overhead conveyors',
    notFor: 'Any falling-object hazard — use full hard hat',
    fitNotes: 'Commonly styled as baseball cap — do not confuse with hard hat; colour coding per area policy',
    comfortRating: 'Excellent',
    durationLimit: 'Replace after any significant impact',
  },
  {
    name: 'High-Visibility Vest / Jacket',
    category: 'High-Visibility & Body',
    standard: 'ANSI/ISEA 107 / EN ISO 20471 (Class 1–3)',
    hazard: 'Struck-by vehicle, mobile equipment, forklift traffic',
    protection: 'Visibility: Class 2 = 775 cm² fluorescent + 201 cm² retroreflective minimum',
    heatResist: 'Limited',
    chemResist: 'Poor',
    requiredFor: 'Any workspace with forklift/vehicle traffic, outdoor construction, roadside work',
    notFor: 'Near open flame without FR-rated hi-vis; rotating machinery (loose fit)',
    fitNotes: 'Class 3 required for roadway/night work; Class 2 for most industrial; wash per label to maintain retroreflectivity',
    comfortRating: 'Excellent',
    durationLimit: 'Replace when reflective strips peel or fluoro fabric fades (wash >25 cycles typically reduces rating)',
  },
  {
    name: 'Flame-Resistant (FR) Clothing',
    category: 'High-Visibility & Body',
    standard: 'NFPA 70E / EN ISO 11612; cal/cm² arc rating',
    hazard: 'Arc flash, flash fire, molten metal splash, welding spatter',
    protection: 'Arc Thermal Performance Value (ATPV): CAT 1=4 cal/cm², CAT 4=40+ cal/cm²',
    heatResist: 'Excellent',
    chemResist: 'Moderate',
    requiredFor: 'Welding, plasma cutting, electrical panel work ≥50V, hot work, foundry',
    notFor: 'FR clothing worn over synthetic base layers (synthetic melts to skin); proper FR layering required',
    fitNotes: 'Never launder FR garments with fabric softener (reduces FR properties); inspect for holes or repairs; CAT level must exceed incident energy analysis',
    comfortRating: 'Moderate',
    durationLimit: 'FR properties persist for garment lifetime if washed correctly; inspect for physical damage quarterly',
  },
  {
    name: 'Welding Jacket / Apron',
    category: 'High-Visibility & Body',
    standard: 'AWS F2.2 / EN ISO 11611',
    hazard: 'Welding spatter, molten metal, UV/IR radiation to torso',
    protection: 'Class 1 (low spatter, limited position); Class 2 (all-position welding)',
    heatResist: 'Excellent',
    chemResist: 'Poor',
    requiredFor: 'MIG/TIG/Stick welding, plasma cutting, hot metal cutting',
    notFor: 'Chemical splash; fine dust hazards',
    fitNotes: 'Split leather for flexibility; full leather or aluminised for foundry; bib apron protects torso/thighs for bench welding',
    comfortRating: 'Moderate',
    durationLimit: 'Inspect for burn-through holes after each session; replace apron strings if frayed',
  },
];

// ── Hazard-to-PPE matrix data ────────────────────────────────────────────────
const HAZARD_MATRIX = [
  {
    hazard: 'Grinding / Cutting (Angle Grinder)',
    eye: 'Required', face: 'Required', hearing: 'Required',
    resp: 'Recommended', hands: 'Recommended', body: 'Recommended',
    notes: 'Face shield over goggles; FR clothing within 3 m of sparks',
  },
  {
    hazard: 'Lathe / Mill Operation',
    eye: 'Required', face: 'Recommended', hearing: 'Recommended',
    resp: 'Situational', hands: 'No gloves on chuck', body: 'Optional',
    notes: 'No gloves near rotating chuck/spindle — entanglement risk; loose clothing tucked in',
  },
  {
    hazard: 'Welding (MIG / TIG / Stick)',
    eye: 'Required (helmet)', face: 'Required', hearing: 'Recommended',
    resp: 'Required', hands: 'Required', body: 'Required',
    notes: 'P100/OV respirator for Mn fumes; FR clothing mandatory; shade DIN 10–13',
  },
  {
    hazard: 'Plasma Cutting',
    eye: 'Required (shade 5–8)', face: 'Required', hearing: 'Required',
    resp: 'Required', hands: 'Required', body: 'Recommended',
    notes: 'UV/IR and metal fumes; hearing >95 dB common; fume extraction critical',
  },
  {
    hazard: 'Sanding / Wood/Metal',
    eye: 'Required', face: 'Optional', hearing: 'Recommended',
    resp: 'Required', hands: 'Recommended', body: 'Optional',
    notes: 'N95 minimum; hardwood/MDF dust = carcinogen — P100 preferred; RPM tool speed check',
  },
  {
    hazard: 'Drilling / Tapping',
    eye: 'Required', face: 'Optional', hearing: 'Optional',
    resp: 'Optional', hands: 'No gloves on drill', body: 'Optional',
    notes: 'No gloves near rotating drill bit; chips redirect from face with shield; coolant splash = goggles',
  },
  {
    hazard: 'Chemical Handling (Solvents/Acids)',
    eye: 'Required (goggles)', face: 'Required', hearing: 'No',
    resp: 'Required (OV)', hands: 'Required (chemical)', body: 'Recommended',
    notes: 'Indirect-vent goggles; half-face OV respirator; nitrile/neoprene/butyl gloves per SDS',
  },
  {
    hazard: 'Spray Painting / Coating',
    eye: 'Required', face: 'Optional', hearing: 'Optional',
    resp: 'Required (OV/P100)', hands: 'Required', body: 'Recommended',
    notes: 'Isocyanate-containing coatings require supplied-air in confined spaces; anti-static for flammable sprays',
  },
  {
    hazard: 'Sheet Metal Fabrication',
    eye: 'Required', face: 'Recommended', hearing: 'Recommended',
    resp: 'Optional', hands: 'Required (cut-resistant)', body: 'Recommended',
    notes: 'A4+ cut-resistant gloves; metatarsal boots; hi-vis near press brake/shear operators',
  },
  {
    hazard: 'Forklift / Material Handling Area',
    eye: 'Optional', face: 'No', hearing: 'Optional',
    resp: 'Optional', hands: 'Recommended', body: 'Required (hi-vis)',
    notes: 'Class 2+ hi-vis mandatory; steel-toe boots; head protection in overhead crane zones',
  },
  {
    hazard: 'Foundry / Casting',
    eye: 'Required (goggles)', face: 'Required (shield)', hearing: 'Required',
    resp: 'Required', hands: 'Required (heat)', body: 'Required (FR/aluminised)',
    notes: 'Leather gaiters or spats over boots; synthetic fabrics prohibited near molten metal',
  },
  {
    hazard: 'Electrical Panel / Arc Flash Work',
    eye: 'Required (arc-rated)', face: 'Required (arc-rated)', hearing: 'Optional',
    resp: 'Optional', hands: 'Required (rubber insulating)', body: 'Required (FR CAT 2+)',
    notes: 'NFPA 70E arc flash hazard analysis mandatory; rubber insulating gloves with leather protectors',
  },
];

// ── Standards reference data ─────────────────────────────────────────────────
const STANDARDS = [
  { code: 'ANSI Z87.1', scope: 'Eye & Face', desc: 'Occupational Eye and Face Protection — USA standard covering impact, splash, dusty environments, welding. Requires "Z87+" or "Z87-2+" marking.', agency: 'ANSI/ISEA' },
  { code: 'EN ISO 16321 / EN 166', scope: 'Eye & Face', desc: 'European eye protection framework; markings for optical class, field of view, protection type (1=spectacles, 3=goggles, 4=face shield). Replaces EN 166 series.', agency: 'CEN/ISO' },
  { code: 'EN 175 / ANSI Z49.1', scope: 'Welding PPE', desc: 'Welding helmets, goggles, and hand shields. ANSI Z49.1 also covers safe practices for welding and cutting environments.', agency: 'CEN / AWS' },
  { code: 'EN 397 / ANSI Z89.1', scope: 'Head Protection', desc: 'Industrial safety helmets. ANSI Z89.1: Type I (top only) vs Type II (lateral), Class E/G/C. EN 397 covers similar plus optional lateral deformation, molten metal, electrical tests.', agency: 'ANSI/CEN' },
  { code: 'EN 812', scope: 'Head Protection', desc: 'Industrial bump caps — protection from striking stationary objects only. NOT a substitute for hard hats.', agency: 'CEN' },
  { code: 'ANSI S3.19 / EN 352', scope: 'Hearing Protection', desc: 'USA: NRR (Noise Reduction Rating) methodology. EU: SNR (Single Number Rating) for earmuffs (EN 352-1) and earplugs (EN 352-2). OSHA derate NRR by 50% for real-world estimation.', agency: 'ANSI / CEN' },
  { code: 'NIOSH 42 CFR 84 / EN 149', scope: 'Respiratory', desc: 'USA: N95/N99/N100/P100/R95 classifications by filter efficiency and oil resistance. EU: FFP1/FFP2/FFP3. N95 ≈ FFP2; N100/P100 ≈ FFP3.', agency: 'NIOSH / CEN' },
  { code: 'EN 374 / ASTM D6978', scope: 'Hand Protection', desc: 'Chemical-resistant gloves: EN 374-1 (terminology), -2 (penetration), -3 (permeation), -4 (degradation). ASTM D6978 specific to chemo-therapy drug gloves.', agency: 'CEN / ASTM' },
  { code: 'ANSI/ISEA 105 / EN 388', scope: 'Hand Protection', desc: 'Mechanical hazard gloves. ANSI: A1–A9 cut levels + impact, puncture ratings. EN 388: 4-digit code for abrasion/cut/tear/puncture, plus TDM cut (letter A–F) and impact (P).', agency: 'ANSI/ISEA / CEN' },
  { code: 'EN 407', scope: 'Hand Protection', desc: '6-digit code for thermal hazard gloves: (1) burning, (2) contact heat, (3) convective heat, (4) radiant heat, (5) small molten metal splash, (6) large molten metal splash. Each digit 0–4.', agency: 'CEN' },
  { code: 'EN 12477', scope: 'Hand Protection', desc: 'Welding gloves specifically. Type A: reduced dexterity, high protection. Type B: higher dexterity, lower protection (TIG welding).', agency: 'CEN' },
  { code: 'ASTM F2413 / EN ISO 20345', scope: 'Foot Protection', desc: 'ASTM F2413: toe (75/50), metatarsal (Mt75), puncture resistance, EH electrical, conductive, ESD designations. EN ISO 20345: S1–S5 grades plus additional designations (A, CI, WRU, HRO, etc.)', agency: 'ASTM / ISO' },
  { code: 'ANSI/ISEA 107 / EN ISO 20471', scope: 'Hi-Visibility', desc: 'High-visibility safety apparel. Three classes based on background material area and retroreflective tape. Class 3 required for roadway; Class 2 typical for industrial. EN: Class 1/2/3 equivalent.', agency: 'ANSI/ISEA / ISO' },
  { code: 'NFPA 70E / EN ISO 11612', scope: 'FR / Body', desc: 'NFPA 70E: Arc flash protection — CAT 1–4 based on cal/cm² incident energy. EN ISO 11612: FR clothing A1/A2 (flame spread), B (convective), C (radiant), D (molten aluminium splash), E (molten iron), F (contact heat).', agency: 'NFPA / ISO' },
  { code: 'EN ISO 11611', scope: 'FR / Body', desc: 'Protective clothing for welding and allied processes. Class 1: lower risk (TIG, micro-plasma). Class 2: higher risk (MIG, MAG, stick, plasma cutting, heavy spatter).', agency: 'ISO' },
  { code: 'OSHA 29 CFR 1910.132–138', scope: 'Regulatory (USA)', desc: 'Subpart I — Personal Protective Equipment: hazard assessment (132), eye/face (133), respiratory (134), head (135), foot (136), electrical (137), hand (138). Requires employer PPE hazard assessment and training.', agency: 'OSHA' },
];

// ── Noise level reference ────────────────────────────────────────────────────
const NOISE_LEVELS = [
  { source: 'Conversation', db: '60–65', action: 'No protection' },
  { source: 'Power Drill', db: '85–95', action: 'Hearing protection recommended' },
  { source: 'Angle Grinder', db: '95–100', action: 'Hearing protection required (>85 dB TWA)' },
  { source: 'Impact Wrench', db: '100–103', action: 'Hearing protection required' },
  { source: 'Router / Planer', db: '100–110', action: 'Hearing protection required' },
  { source: 'Chipping Hammer', db: '103–115', action: 'Double protection (muffs + plugs) may be needed' },
  { source: 'Plasma Cutter', db: '95–100', action: 'Hearing protection required; welding helmet may attenuate' },
  { source: 'Powder-Actuated Tool', db: '130–140 (impulse)', action: 'Impulse: damage from single event; double protection required' },
];

// ── Main PPE matrix table ────────────────────────────────────────────────────
function PPEMatrixTable() {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '8px 10px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.82rem', whiteSpace: 'nowrap',
  };
  const tdBase = {
    padding: '8px 10px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.82rem',
  };
  const cols = [
    { key: 'hazard', label: 'Hazard / Task', w: '200px' },
    { key: 'eye', label: 'Eye' },
    { key: 'face', label: 'Face/Head' },
    { key: 'hearing', label: 'Hearing' },
    { key: 'resp', label: 'Respiratory' },
    { key: 'hands', label: 'Hands' },
    { key: 'body', label: 'Body' },
    { key: 'notes', label: 'Key Notes', w: '220px' },
  ];
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr>{cols.map(c => <th key={c.key} style={{ ...thStyle, width: c.w || 'auto' }}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {HAZARD_MATRIX.map((row, i) => (
            <tr key={i}>
              {cols.map(c => (
                <td key={c.key} style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: c.key === 'hazard' ? '600' : '400', color: c.key === 'hazard' ? '#145a24' : '#0a1f0e', fontFamily: c.key === 'hazard' || c.key === 'notes' ? "'Georgia', serif" : 'inherit' }}>
                  {c.key !== 'hazard' && c.key !== 'notes'
                    ? <RatingBadge value={row[c.key]} />
                    : row[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Detailed PPE item table ──────────────────────────────────────────────────
function PPEDetailTable() {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '8px 10px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.82rem',
  };
  const tdBase = {
    padding: '8px 10px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top',
    fontSize: '0.82rem',
  };
  const catColors = {
    'Eye & Face Protection': '#c6ecd9',
    'Hearing Protection':    '#c6ecd9',
    'Respiratory Protection':'#fde8c0',
    'Hand Protection':       '#e8d5f5',
    'Foot Protection':       '#fde2d0',
    'Head Protection':       '#d5eaf5',
    'High-Visibility & Body':'#f5f0c6',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr>
            {['Item', 'Category', 'Standard', 'Primary Hazard', 'Protection Level', 'Required For', 'NOT For / Limitations', 'Fit / Maintenance Notes'].map(h => (
              <th key={h} style={thStyle}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {PPE_ITEMS.map((p, i) => (
            <tr key={i}>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '700', color: '#145a24', fontFamily: "'Georgia', serif", whiteSpace: 'nowrap' }}>{p.name}</td>
              <td style={{ ...tdBase, background: catColors[p.category] || '#f5faf7', fontWeight: '600', fontSize: '0.78rem', color: '#1a2a1a', whiteSpace: 'nowrap' }}>{p.category}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontFamily: "'Courier New', monospace", fontSize: '0.78rem' }}>{p.standard}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{p.hazard}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{p.protection}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', color: '#1a3a1a' }}>{p.requiredFor}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', color: '#5a1a1a' }}>{p.notFor}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontStyle: 'italic', fontSize: '0.8rem' }}>{p.fitNotes}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Standards table ──────────────────────────────────────────────────────────
function StandardsTable() {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '8px 12px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.85rem',
  };
  const tdBase = {
    padding: '8px 12px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top', fontSize: '0.85rem',
  };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: '160px' }}>Standard / Code</th>
            <th style={{ ...thStyle, width: '140px' }}>PPE Scope</th>
            <th style={thStyle}>Description</th>
            <th style={{ ...thStyle, width: '100px' }}>Agency</th>
          </tr>
        </thead>
        <tbody>
          {STANDARDS.map((s, i) => (
            <tr key={i}>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontFamily: "'Courier New', monospace", fontWeight: '700', color: '#145a24', fontSize: '0.82rem' }}>{s.code}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#1e7a36', fontSize: '0.82rem' }}>{s.scope}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{s.desc}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#5a1a5a', fontSize: '0.82rem' }}>{s.agency}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Noise level table ────────────────────────────────────────────────────────
function NoiseTable() {
  const thStyle = {
    background: '#1e7a36', color: '#fff', padding: '8px 14px',
    textAlign: 'left', fontWeight: '700', fontSize: '0.85rem',
  };
  const tdBase = { padding: '8px 14px', borderBottom: '1px solid #b8dfc2', verticalAlign: 'top', fontSize: '0.85rem' };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
      <thead>
        <tr>
          <th style={thStyle}>Tool / Source</th>
          <th style={thStyle}>Typical dB(A)</th>
          <th style={thStyle}>Hearing Protection Action</th>
        </tr>
      </thead>
      <tbody>
        {NOISE_LEVELS.map((n, i) => {
          const isHigh = parseInt(n.db) >= 85 || n.db.includes('130');
          return (
            <tr key={i}>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontWeight: '600', color: '#145a24' }}>{n.source}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7', fontFamily: "'Courier New', monospace", color: isHigh ? '#7a0010' : '#1a5c1a', fontWeight: '700' }}>{n.db}</td>
              <td style={{ ...tdBase, background: i % 2 === 0 ? '#e8f5ec' : '#f5faf7' }}>{n.action}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ── Main export ──────────────────────────────────────────────────────────────
export default function WorkshopPPE() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Workshop Personal Protective Equipment (PPE) Reference</h1>
      <div style={S.subtitle}>
        Comprehensive reference for PPE selection, standards compliance, hazard matching, and inspection requirements
        for machine shops, fabrication, welding, woodworking, and general industrial workshop environments.
        Always perform a site-specific hazard assessment (OSHA 29 CFR 1910.132 / local equivalent) and consult Safety Data Sheets for chemical hazards.
      </div>

      {/* Legend */}
      <div style={S.legendBox}>
        <strong style={{ fontSize: '0.85rem', color: '#145a24', alignSelf: 'center' }}>Requirement Level Key:</strong>
        {[
          ['Required', RATING_COLORS['Required']],
          ['Recommended', RATING_COLORS['Recommended']],
          ['Situational', RATING_COLORS['Situational']],
          ['Optional', RATING_COLORS['Optional']],
          ['High', RATING_COLORS['High']],
          ['Medium', RATING_COLORS['Medium']],
          ['Low', RATING_COLORS['Low']],
          ['Yes', RATING_COLORS['Yes']],
          ['No', RATING_COLORS['No']],
          ['Partial', RATING_COLORS['Partial']],
        ].map(([label, c]) => (
          <div key={label} style={S.legendItem}>
            <div style={{ ...S.legendDot, background: c.bg, border: `1px solid ${c.border}` }} />
            <span style={{ color: c.text, fontWeight: '600' }}>{label}</span>
          </div>
        ))}
      </div>

      {/* ── Section 1: Hazard Matrix ── */}
      <h2 style={S.h2}>Hazard-to-PPE Selection Matrix</h2>
      <p style={S.p}>
        The table below maps common workshop operations to PPE requirements across all categories.
        "Required" indicates that the hazard is well-established and PPE is mandated by OSHA/EU-OSHA regulations
        for that exposure. "Recommended" reflects best practice where regulation may not specify but risk warrants protection.
        "Situational" means protection depends on process parameters (coolant use, material type, duration).
      </p>
      <div style={S.dangerBox}>
        ⛔ <strong>NEVER wear gloves near rotating machinery</strong> (lathes, drill presses, milling machines, grinders).
        Entanglement can result in degloving or amputation injuries in milliseconds. This overrides all general glove guidance.
      </div>
      <PPEMatrixTable />

      {/* ── Section 2: Detailed PPE Reference ── */}
      <h2 style={S.h2}>Detailed PPE Item Reference</h2>
      <p style={S.p}>
        Each PPE type is characterised by its applicable standard, primary protection level, required use cases, known
        limitations, and fit/maintenance guidance. Protection levels reflect performance ratings under the cited standard —
        always verify specific product certification markings before procurement.
      </p>
      <div style={S.noteBox}>
        ℹ️ <strong>USA vs EU Standards:</strong> USA (OSHA/ANSI/NIOSH) and European (EN/ISO/CEN) standards generally
        cover equivalent hazard classes but use different marking schemes and test methods. Products certified to
        one system are not automatically accepted under the other. For export or multinational operations, confirm
        the applicable jurisdictional standard.
      </div>
      <PPEDetailTable />

      {/* ── Section 3: Noise / Hearing ── */}
      <h2 style={S.h2}>Workshop Noise Levels & Hearing Protection Thresholds</h2>
      <p style={S.p}>
        Noise-induced hearing loss (NIHL) is permanent and cumulative. OSHA mandates hearing conservation programs
        when 8-hour time-weighted average (TWA) exposures reach 85 dB(A), with mandatory protection at 90 dB(A) TWA
        (29 CFR 1910.95). EU Directive 2003/10/EC sets lower action values (80 dB(A) LEX,8h). NRR ratings on USA
        hearing protectors should be derated by 50% per OSHA guidance to reflect real-world performance.
      </p>
      <NoiseTable />
      <InfoBox title="Effective NRR Calculation (OSHA Method)" items={[
        'Effective NRR = (Labelled NRR − 7) ÷ 2',
        'Example: Foam earplug NRR 33 → (33 − 7) ÷ 2 = 13 dB real-world attenuation',
        'If TWA = 100 dB, protected exposure ≈ 100 − 13 = 87 dB — still above 85 dB → higher NRR or dual protection needed',
        'Double protection (muffs + plugs): add 5 dB to the higher of the two NRR values (not additive)',
      ]} />

      {/* ── Section 4: Respiratory Protection ── */}
      <h2 style={S.h2}>Respiratory Hazard Classification & Filter Selection</h2>
      <p style={S.p}>
        Respiratory protection is the most complex PPE category due to the variety of airborne hazards (particulates,
        vapours, gases, biological), the critical role of fit, and the medical suitability requirement. OSHA 1910.134
        requires a written respiratory protection programme including hazard characterisation, fit testing, medical evaluation,
        training, and maintenance for all respirators used in the workplace (even voluntary use has partial requirements).
      </p>
      <Table2 rows={[
        ['Nuisance Dust (OEL > 0.5 mg/m³)', 'Disposable filtering facepiece: N95 (NIOSH) or FFP2 (EN 149)\nFor wood, metal fines, concrete, plaster, non-toxic mineral dusts\nNo oil aerosols: use N or R series; oil aerosols present: P series'],
        ['Fine Respirable Dust / Silica', 'N100 or P100 (NIOSH) or FFP3 (EN 149)\nCrystalline silica: OSHA PEL 50 µg/m³; silicosis risk from stone, concrete, brick cutting/grinding\nEngineering controls (wet methods, LEV) should be primary; respirator is supplementary'],
        ['Metal Welding Fume (Mn, Cr, Ni)', 'P100 half-face or full-face respirator; PAPR for extended welding\nManganese: neurological effects (manganism) at cumulative exposure; OSHA PEL 0.2 mg/m³ ceiling\nStainless steel welding: Cr(VI) fume, a Group 1 carcinogen — P100 + OV combination recommended'],
        ['Organic Vapours (Solvents)', 'Half-face respirator with OV (organic vapour) cartridges\nUsed for: paint thinners, acetone, MEK, toluene, xylene, naphtha\nChange cartridges per OSHA change-out schedule; use ESLI (end-of-service-life indicator) if available'],
        ['Isocyanates (2K paints, PU coatings)', 'Supplied-air respirator (SAR) or SCBA in confined spaces; minimum P100 + OV in open ventilated areas\nIsocyanates can cause occupational asthma and sensitisation at very low concentrations (OSHA PEL 0.02 ppm)\nMedical surveillance required for continued exposed workers'],
        ['Lead Dust / Paint Removal', 'P100 half or full-face; PAPR preferred for demolition/abrasive blasting\nBlood lead monitoring required under OSHA 1910.1025; full change room / hygiene facilities required\nDisposable overalls + head cover to prevent contamination transfer'],
        ['Asbestos (legacy buildings)', 'Full-face P100 minimum; negative pressure or PAPR for Class I/II removal\nOSHA 1910.1001 governs; air monitoring and personal air sampling required; licensed abatement for high-risk work'],
        ['Oxygen-Deficient / IDLH Atmospheres', 'Self-Contained Breathing Apparatus (SCBA) or Supplied-Air Respirator (SAR) only\nO₂ < 19.5%: IDLH by definition; SCBA with fully encapsulating suit for CBRN/unknown hazards\nNo air-purifying respirator (APF) is adequate — cartridges do not supply oxygen'],
      ]} />

      {/* ── Section 5: Glove Selection Guide ── */}
      <h2 style={S.h2}>Glove Selection by Hazard Type</h2>
      <p style={S.p}>
        Glove selection requires matching material, thickness, and construction to the specific hazard.
        No single glove protects against all hazards — mechanical cut resistance and chemical permeation resistance
        are largely independent properties. Always consult the chemical manufacturer's permeation data for
        the specific glove material and the chemicals in use.
      </p>
      <div style={S.dangerBox}>
        ⛔ <strong>ROTATING MACHINERY RULE:</strong> Never wear any gloves when operating lathes, drill presses, mills, grinders,
        or any rotating tool. This applies even when the machine is slowing to a stop. Remove gloves before operating.
      </div>
      <Table2 rows={[
        ['Nitrile (0.1–0.4 mm disposable)', 'Best for: oils, fuels, greases, many solvents, biological fluids\nNot for: ketones, esters, strong aromatics — breakthrough time <15 min\nTypical use: parts degreasing, coolant handling, fluid sampling, light chemical handling'],
        ['Nitrile (0.5–1.0 mm heavy-duty)', 'Extended contact with petroleum products; automotive, machining\nHigher puncture and abrasion resistance than disposable grade\nNote: thicker = less dexterity; choose minimum thickness adequate for task'],
        ['Neoprene', 'Best for: acids, bases, alcohols, oil-based materials\nModerate protection against many organic solvents\nTypical use: battery acid, caustic soda handling, surface prep chemicals'],
        ['Butyl Rubber', 'Best for: ketones (acetone, MEK), esters (ethyl acetate), aldehydes\nHighest resistance to gas permeation of any glove material\nTypical use: epoxy solvent mixing, strong ketone-based strippers'],
        ['Natural Latex', 'Good for: dilute acids/bases, alcohols, water-based chemicals\nAvoid: latex-sensitive personnel (allergy risk); avoid solvents and oils\nNote: double-gloving with latex outer over nitrile inner for mixed hazards'],
        ['HPPE / Steel Fibre (Cut-Resistant)', 'ANSI A4–A9 cut resistance; EN 388 TDM rating D–F\nFor: sheet metal edges, stamping, glass, blade changes\nNOT for: chemicals (permeation data not established for cut-resistant materials)'],
        ['Leather (Cowhide / Pig skin)', 'Best for: general abrasion, rough material handling, moderate heat\nNot for: chemicals, wet environments, precision work\nTypical use: structural steel, casting handling, welding (lower heat tasks)'],
        ['Aluminised / Kevlar (Heat)', 'EN 407 Level 3–4 heat resistance; radiant heat protection\nFor: foundry, furnace operations, continuous contact heat >200 °C\nNote: aluminised reflects radiant heat; kevlar for contact heat — different hazard modes'],
        ['Anti-Vibration Gloves', 'Partial reduction of HAV (hand-arm vibration) transmission\nDo NOT rely on gloves alone: OSHA/EU limits require tool substitution as primary control\nISO 10819 standard; most effective at mid-high frequency ranges (150–1000 Hz)'],
        ['Electrical Insulating Rubber Gloves', 'ASTM D120 / IEC 60903 Class 00–4 (500V–40kV rated voltage)\nPrimary electrical protection; always use with leather protector outer glove\nAnnual dielectric testing + pre-use air inflation test required; replace if any crack or puncture'],
      ]} />

      {/* ── Section 6: Inspection & Replacement ── */}
      <h2 style={S.h2}>PPE Inspection, Maintenance & Replacement Schedule</h2>
      <p style={S.p}>
        All PPE must be inspected before each use and maintained per manufacturer guidelines. Damaged PPE must be
        removed from service immediately — do not attempt field repair on impact-protective or electrically-rated PPE.
        Shelf life applies to elastomeric and polymer components that degrade over time regardless of use.
      </p>
      <Table2 rows={[
        ['Safety Glasses / Goggles', 'Before each use: check for scratches reducing optical clarity, cracked lens, broken side shields, degraded foam seal (goggles)\nReplace: when any lens damage present; foam seal no longer seals; after chemical exposure compromises frame'],
        ['Welding Helmet (Auto-Darkening)', 'Before each use: test auto-darkening function by exposing to arc simulator or brief light source; check battery/solar cell\nReplace lens: when cracked, pitted, or arc sensitivity reduced; ADF cartridge per manufacturer interval'],
        ['Hard Hat / Safety Helmet', 'Before each use: inspect shell for cracks, dents, UV chalking; check suspension for fraying, cracks, missing components\nMandatory replacement: 5 years from manufacture date (shell); 1 year (suspension) — dates stamped inside shell\nImmediate replacement after: any impact, even without visible damage; chemical exposure beyond rating'],
        ['Foam Earplugs', 'Single-use only — do not reuse. If reusable: inspect for dirt, distortion, cracks; clean per manufacturer instructions; replace when no longer recover to original shape'],
        ['Earmuffs', 'Monthly: inspect cushions for hardening, cracking, or loss of sealing ability; check headband spring tension\nReplace cushions: every 6–12 months depending on use intensity; never compress over safety glasses without spectacle-notch cups'],
        ['N95 / FFP2 Disposable Respirator', 'Inspect before each use: check straps, nose wire, filter media for damage; perform positive/negative pressure user seal check\nReplace immediately: if damaged, wet, dirty, contaminated, or breathing resistance noticeably increased\nSingle-use in contaminated environments'],
        ['Half-Face Respirator', 'Before each use: inspect face seal for cracks/deformation; check exhalation valve; verify cartridge installation and date\nCartridge change: per OSHA change-out schedule or ESLI; OV cartridges absorb vapour even when not in use — date on opening\nFace seal: replace if silicone degrades or no longer conforms; fit test annually or when facial structure changes'],
        ['Chemical-Resistant Gloves', 'Before each use: visual inspection + air inflation test (pinch glove wrist, roll to pressurize) for pinholes\nReplace: when any perforation, discoloration from chemical, stiffening, tackiness, or loss of elasticity detected\nNitrile disposable: single-use for any chemical exposure'],
        ['Cut-Resistant Gloves', 'Before each use: inspect palm and fingers for cut-through or thinning; check stitching at seams\nReplace: when visible cuts or abrasion through fibres; HPPE fibres: damaged strands reduce protection below rating'],
        ['Safety Footwear', 'Monthly: inspect toe cap integrity (visible denting), sole tread depth (replace <1.6 mm), upper for cuts/delamination\nReplace: after any significant impact to toe cap; when EH rating integrity is in doubt (cracks in sole); when sole separates from upper'],
        ['FR / Welding Clothing', 'Before each use: inspect for holes, tears, burn-through areas; check for hydrocarbon contamination (laundering required before re-use near ignition sources)\nNever launder with fabric softener — disrupts FR treatment; follow care label exactly; inspect seam integrity quarterly'],
        ['High-Visibility Vest', 'Inspect retroreflective strips for peeling, separation, or contamination; fluoro background for fading\nReplace: when retroreflective area falls below class minimum after washing or wear; typically 12–24 months of regular use'],
      ]} />

      {/* ── Section 7: Standards Reference ── */}
      <h2 style={S.h2}>PPE Standards & Regulatory Reference</h2>
      <p style={S.p}>
        PPE performance is governed by a combination of voluntary consensus standards (ANSI, ASTM, ISO, EN) and mandatory
        regulatory requirements (OSHA, EU Directive 89/686/EEC / Regulation 2016/425). The table below covers the primary
        standards applicable to workshop PPE in the USA and EU. Standards are regularly revised — always verify the current
        version is being applied.
      </p>
      <StandardsTable />

      {/* ── Section 8: Selection Guidance ── */}
      <h2 style={S.h2}>PPE Selection Decision Guidance</h2>
      <InfoBox title="Eye & Face Protection Selection" items={[
        'Machining (chips/swarf, dry) → Safety glasses ANSI Z87.1 with side shields (minimum)',
        'Machining with coolant / chemical splash → Indirect-vent chemical goggles + face shield',
        'Grinding / cutting (sparks, fragments) → Safety goggles + full face shield over glasses',
        'Welding MIG/Stick → Auto-darkening welding helmet shade 10–13; TIG shade 9–12',
        'Plasma cutting → Welding helmet shade 5–8 + hearing protection',
        'Laser work → Wavelength-specific laser safety goggles; never substitute regular glasses',
        'UV curing operations → UV-blocking goggles rated to source wavelength',
      ]} />
      <InfoBox title="Hearing Protection Selection" items={[
        'Intermittent grinding, drilling (<60 min/shift) → Earmuffs for convenience (easy on/off)',
        'Continuous noise exposure (full shift) → High-NRR foam earplugs + earmuffs if TWA near limit',
        'Communication required (supervisor interaction) → Electronic earmuffs with amplification + cut-off',
        'Hot, sweaty environments → Foam earplugs preferred over earmuffs (heat build-up under cups)',
        'Impulse noise only (pneumatic tools, powder-actuated) → Earmuffs rated for impulse attenuation',
        'If unsure: use industrial hygiene noise dosimetry to establish TWA before selecting protection level',
      ]} />
      <InfoBox title="Hand Protection Selection Decision Tree" items={[
        'Rotating machinery (lathe, drill, mill, grinder) → NO gloves — entanglement risk',
        'Sheet metal / sharp edges → ANSI A4+ cut-resistant gloves; leather gauntlet for heavy edges',
        'Welding → EN 12477 Type A leather welding gloves; Type B for TIG (dexterity)',
        'General abrasion / material handling → Leather or HPPE palm-coated work gloves',
        'Chemical handling → Match glove material to SDS chemical resistance data (nitrile / neoprene / butyl)',
        'Electrical work → ASTM D120 rubber insulating gloves + leather protector, voltage class matched to task',
        'Heat / foundry → EN 407 rated gloves; aluminised for radiant heat; kevlar for contact heat',
        'Food-grade coolant / cutting fluid → Nitrile exam gloves or disposable vinyl (change frequently)',
      ]} />
      <InfoBox title="Respiratory Protection Selection Decision Tree" items={[
        'Non-toxic dust (wood, concrete, general) → N95/FFP2 disposable; P100 for fine respirable dust, silica',
        'Welding fume (general) → P100 half-face or PAPR; upgrade to combo OV+P100 for stainless/galvanised',
        'Organic vapours (solvents, paints) → Half-face with OV cartridges; verify cartridge type matches vapour',
        'Isocyanates / highly toxic → Supplied-air preferred; at minimum full-face P100+OV with medical surveillance',
        'High-volume abrasive blasting / demolition → PAPR or SAR (hood-style); consider full enclosure with extraction',
        'Confined space / unknown atmosphere → SCBA; no air-purifying unit until atmosphere is characterised',
        'All tight-fitting respirators → Annual fit test required; medical clearance required before use',
      ]} />

      {/* ── Section 9: Hierarchy of Controls ── */}
      <h2 style={S.h2}>Hierarchy of Controls & PPE as Last Resort</h2>
      <p style={S.p}>
        OSHA and all major occupational health frameworks place PPE at the bottom of the hierarchy of controls.
        PPE does not eliminate hazards — it places a barrier between the worker and the hazard. Failures in fit,
        donning/doffing, maintenance, or compliance immediately reduce protection. Engineering and administrative
        controls should be implemented first, with PPE used to control residual risk.
      </p>
      <Table2 rows={[
        ['1. Elimination', 'Remove the hazard entirely\nExample: substitute water-based coating for solvent-based; use CNC press brake instead of manual cut'],
        ['2. Substitution', 'Replace with a less hazardous material or process\nExample: water-miscible cutting fluid instead of neat oil; lower-VOC solvent; pre-cut stock eliminating grinding'],
        ['3. Engineering Controls', 'Physical barriers, ventilation, guarding — do not rely on worker behaviour\nExample: enclosed CNC with mist collector; local exhaust ventilation (LEV) at grinding wheel; acoustic enclosures; interlocked machine guards'],
        ['4. Administrative Controls', 'Work practices, training, scheduling to reduce exposure duration/frequency\nExample: job rotation to reduce vibration exposure; wet methods for silica dust; two-person safety rule for confined spaces'],
        ['5. PPE (Last Line of Defence)', 'Protects the individual — does not reduce hazard in the environment\nEffectiveness depends entirely on: correct selection, proper fit, correct donning/doffing, regular inspection and maintenance, compliance\nMust be used when engineering and administrative controls cannot reduce hazard below acceptable limits'],
      ]} />
      <div style={S.noteBox}>
        ⚠️ <strong>PPE Programme Requirements (OSHA 29 CFR 1910.132):</strong> Employers must conduct a written hazard assessment; select appropriate PPE; provide PPE at no cost to employees; train employees on when and how to use PPE; document training; re-evaluate and retrain when PPE changes or deficiencies are observed.
      </div>
      <InfoBox title="Key Caveats & Important Limitations" items={[
        'All PPE ratings are measured under standardised laboratory test conditions. Real-world performance varies with fit, donning procedure, work task, and condition of equipment.',
        'Standards are version-controlled and regularly revised. Always verify the current applicable version for your jurisdiction and application.',
        'PPE is not a substitute for engineering controls. Use PPE to control residual risk after engineering controls are maximised.',
        'Chemical permeation data for gloves is material- and concentration-specific. A glove rated for acetone at 10% may be inadequate at 100%. Always consult the actual permeation data for the specific glove product and chemical.',
        'Respiratory protection requires a written programme, medical evaluation, fit testing, and training under OSHA 29 CFR 1910.134. Providing respirators without this programme violates federal law in the USA.',
        'Electrical safety PPE (rubber insulating gloves, arc flash clothing) must be inspected, tested, and rated for the specific voltage and incident energy involved. Using Class 0 gloves on a 480V system is inadequate.',
        'Sources: OSHA 29 CFR 1910 Subpart I; ANSI Z87.1-2020; ANSI Z89.1-2014; ANSI/ISEA 105-2016; ASTM F2413-18; ANSI/ISEA 107-2015; NIOSH 42 CFR Part 84; NFPA 70E-2021; EN 166, EN 397, EN 352, EN 388, EN 374, EN 407, EN 12477, EN ISO 11611, EN ISO 11612, EN ISO 20345, EN ISO 20471; AWS F2.2; ISO 10819 (anti-vibration gloves); ACGIH TLV/BEI guidelines. Noise levels are representative values — actual measurements vary with tool condition, work method, and environment.',
      ]} />
    </div>
  );
}